import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FOCUS_MESSAGE_QUERY_KEY } from '#src/constants/message-thread';

export function useThreadFocusSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const rawFocusMessageId = searchParams.get(FOCUS_MESSAGE_QUERY_KEY);

  /** Pushes a history entry so the browser Back button exits thread focus. */
  const expandThread = useCallback(
    (rootId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(FOCUS_MESSAGE_QUERY_KEY, rootId);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  /** Pops the last history entry (same as browser Back after expand). */
  const collapseThread = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /**
   * Removes `focusMessageId` on the current entry without going back.
   * Use for invalid IDs so we do not add or pop history entries incorrectly.
   */
  const replaceStripFocusParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(FOCUS_MESSAGE_QUERY_KEY);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
    searchParams,
    setSearchParams,
  };
}
